/**
 * @file PipelineStagesPage.tsx
 * @description Стадии воронки сделок — раздел «Настроек» (phase09;
 * phase19.31 — минимум по SC50, по образцу стадий заказов).
 *
 * Заголовок — в верхней панели (`CabinetPageHeader`): «назад» к воронкам,
 * название воронки, главное действие «+ Стадия». Список — `StageList`:
 * строка 56, цвет стадии точкой, правка по нажатию, удаление в «⋯»,
 * порядок — перетаскиванием (оптимистично, с откатом при ошибке).
 */

import { arrayMove } from "@dnd-kit/sortable";
import { ListOrdered, Plus } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router";

import { useHasPermission } from "../../../../auth/hooks";
import { StageList } from "../../../../components/crm/pipelines/StageList";
import { StageEditDialog } from "../../../../components/crm/StageEditDialog";
import { Button } from "../../../../components/ui/button";
import { CabinetPageHeader } from "../../../../components/ui/CabinetPageHeader";
import { ListEmpty, ListError, ListSkeleton } from "../../../../components/ui/ListStates";
import { useNotify } from "../../../../components/ui/NotifyProvider";
import { ApiError } from "../../../../lib/api-client";
import {
  deleteStage,
  getPipeline,
  listStages,
  reorderStages,
  type PipelineDto,
  type PipelineStageDto,
} from "../../../../lib/api-crm";
import { pipelineDescription } from "../../../../lib/crm-pipeline-view";
import { pluralRu } from "../../../../lib/plural";

export function PipelineStagesPage() {
  const { slug, pipelineId } = useParams<{ slug: string; pipelineId: string }>();
  const notify = useNotify();
  const canConfig = useHasPermission("SHOP_MANAGE_CRM_CONFIG");
  const [pipeline, setPipeline] = useState<PipelineDto | null>(null);
  const [stages, setStages] = useState<PipelineStageDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<PipelineStageDto | "new" | null>(null);
  // Порядок до перетаскивания — для отката при ошибке сервера.
  const stagesBeforeDrag = useRef<PipelineStageDto[]>([]);

  const refresh = useCallback(async () => {
    if (!pipelineId) return;
    setLoading(true);
    setError(null);
    try {
      const [p, s] = await Promise.all([getPipeline(pipelineId), listStages(pipelineId)]);
      setPipeline(p);
      setStages(s);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить стадии");
    } finally {
      setLoading(false);
    }
  }, [pipelineId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function onReorder(activeId: string, overId: string) {
    if (!pipelineId) return;
    const from = stages.findIndex((s) => s.id === activeId);
    const to = stages.findIndex((s) => s.id === overId);
    if (from < 0 || to < 0) return;
    stagesBeforeDrag.current = stages;
    const next = arrayMove(stages, from, to);
    setStages(next);
    try {
      setStages(await reorderStages(pipelineId, next.map((s, idx) => ({ id: s.id, sortOrder: (idx + 1) * 10 }))));
    } catch (err) {
      setStages(stagesBeforeDrag.current);
      notify.error(err instanceof ApiError ? err.message : "Не удалось изменить порядок стадий");
    }
  }

  async function onDelete(stage: PipelineStageDto) {
    if (!pipelineId) return;
    if (stage.dealsCount > 0) {
      const deals = `${stage.dealsCount} ${pluralRu(stage.dealsCount, ["сделка", "сделки", "сделок"])}`;
      notify.error(`На стадии «${stage.name}» ${deals}. Уберите стадию в архив через «Изменить»`);
      return;
    }
    if (!window.confirm(`Удалить стадию «${stage.name}»?`)) return;
    try {
      await deleteStage(pipelineId, stage.id);
      notify.success("Стадия удалена");
      void refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось удалить стадию");
    }
  }

  const back = { to: `/shop/${slug}/crm/pipelines`, label: "К воронкам сделок" };
  const action = canConfig && pipeline ? (
    <Button variant="brand" onClick={() => setEditing("new")}>
      <Plus className="size-4" />
      Стадия
    </Button>
  ) : undefined;

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title={pipeline?.name ?? "Стадии воронки"}
        subtitle={pipelineDescription(pipeline?.description) ?? "Стадии воронки сделок"}
        back={back}
        action={action}
      />

      {error && stages.length === 0 ? (
        <ListError message={error} onRetry={() => void refresh()} />
      ) : loading && stages.length === 0 ? (
        <ListSkeleton rows={5} compact />
      ) : stages.length === 0 ? (
        <ListEmpty
          filtered={false}
          spec={{ icon: ListOrdered, title: "Стадий ещё нет", description: canConfig ? "Добавьте первую кнопкой «+ Стадия»" : undefined }}
        />
      ) : (
        <StageList
          stages={stages}
          canConfig={canConfig}
          onEdit={(stage) => setEditing(stage)}
          onDelete={(stage) => void onDelete(stage)}
          onReorder={(a, o) => void onReorder(a, o)}
        />
      )}

      {editing && pipelineId ? (
        <StageEditDialog
          mode={editing === "new" ? "create" : "edit"}
          pipelineId={pipelineId}
          stage={editing === "new" ? undefined : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void refresh();
          }}
        />
      ) : null}
    </div>
  );
}
